import React from 'react';
import { mergeClassNames } from '../../../../utils';
import styles from './index.module.scss';

/**
 * Fixed component: Fixes the position of the provided element below the header
 * at the top of the screen.
 *
 * `overlay`: An element that is overlayed the fixed content.
 *
 * - the overlay can be **broken below fixed content** (`breakOverlay`) on sizes
 * from tablet and below to make sure the screen real estate becomes less
 * crowded. **adds `min-height: 100%` to the overlay element to ensure that it
 * grows to full height**.
 * - the overlay can also be constrained (`constrainOverlay`) to viewport to
 * ensure that the overlayed content is visible even if the fixed content is
 * bigger (taller) than the viewport
 *
 *
 * @component {React.SFC<{onSubmit:function, text:string}>}
 * @param {Object} props Fixed component props
 * @param {string} [props.id] element id
 * @param {React.ReactNode} [props.children] element to be fixed in the viewport
 * @param {React.ReactNode} [props.overlay] optional overlay
 * @param {boolean} [props.breakOverlay] break overlay below fixed content on portrait layouts
 * @param {boolean} [props.constrainOverlay] constrain the overlay to be within the viewport if the fixed content overlows the viewport
 * @param {boolean} [props.deactivateFixed] deactivate `position: fixed` when true (use together with fx intersection observer)
 * @param {boolean} [props.hide] hide elem when set
 * @param {string} [props.className] optional additional className
 * @param {object} [props.style] optional additional styles
 *
 * @return {React.ReactElement} - React component
 */
export function Fixed({
  id = null,
  children = null,
  overlay = null,
  constrainOverlay = false,
  breakOverlay = false,
  deactivateFixed = false,
  hide = false,
  className = null,
  style = null,
}) {
  return (
    <div
      id={id}
      className={mergeClassNames([
        styles.fixedPositionFullViewport,
        deactivateFixed && styles.deactivateFixed,
        hide && styles.hide,
        className,
      ])}
      style={style}
    >
      <div className={styles.maxWidthContainer}>
        <div className={mergeClassNames([styles.inner, breakOverlay ? styles.break : null])}>
          {children}
          {Boolean(overlay) && (
            <FixedOverlay isConstrained={constrainOverlay} isBreaking={breakOverlay}>
              {overlay}
            </FixedOverlay>
          )}
        </div>
      </div>
    </div>
  );
}

function FixedOverlay({ children, isConstrained = false, isBreaking = false }) {
  return (
    <div
      className={mergeClassNames([
        styles.overlay,
        isConstrained ? styles.constrain : '',
        isBreaking ? styles.break : '',
      ])}
    >
      <div className={styles.scrollableZone}>{children}</div>
    </div>
  );
}
